import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { Download, Upload } from 'lucide-react';

interface ImportMessagesHeaderProps {
  onImport: () => void;
  messages: UserMessage[];
}

export const ImportMessagesHeader = ({ onImport, messages }: ImportMessagesHeaderProps) => {
  const { t } = useTranslation();

  const handleExport = useCallback(() => {
    // Prepare data for export
    const exportData = messages.map(msg => {
      const contact = msg.contactList?.[0];
      
      return {
        'Contact Name': contact?.name || msg.contactName || contact?.formattedTitle || '',
        'Status': msg.status || '',
        'Message': msg.message || '',
        'Due Date': msg.dueDate ? new Date(msg.dueDate).toLocaleDateString() : '',
        'Due Time': msg.dueTime 
          ? new Date(msg.dueTime).toLocaleString() 
          : msg.dueDate 
          ? new Date(msg.dueDate).toLocaleString() 
          : '',
        'Contact Mobile': contact?.mobile || contact?.id || '', // WhatsApp ID is in contact.id
        'Contact First Name': contact?.firstName || '',
        'Contact Last Name': contact?.lastName || '',
        'WhatsApp ID': contact?.id || '', // Explicit WhatsApp ID field
        'Campaign ID': msg.campaignId || '',
        'Retry Count': msg.retryCount || 0,
        'Send Failure Reason': msg.sendFailureReason || '',
        'Sent With': msg.sentWith || '',
        'Is Recurring': msg.isRecurring ? 'Yes' : 'No',
        'Verify Contact': msg.verifyContact ? 'Yes' : 'No',
        'Created At': msg.createdAt ? new Date(msg.createdAt).toLocaleString() : '',
        'Updated At': msg.updatedAt ? new Date(msg.updatedAt).toLocaleString() : '',
      };
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Messages');

    // Auto-size columns
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(key.length, 15)
    }));
    ws['!cols'] = colWidths;

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `user-messages-export-${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
  }, [messages]);

  return (
    <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
          {t('importMessages.pageTitle')}
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
          {t('importMessages.pageDescription')}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={onImport}
          className="flex items-center gap-2"
          variant="outline"
        >
          <Upload className="h-4 w-4" />
          {t('importMessages.header.importFromExcel')}
        </Button>

        <Button
          onClick={handleExport}
          disabled={messages.length === 0}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          {t('importMessages.header.exportToExcel')}
        </Button>
      </div>
    </div>
  );
};

